import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Stack } from '@mui/material';

import CloseIcon from 'shared/icons/Close';
import Checkbox from 'shared/components/Checkbox';
import InfoOutlined from 'shared/icons/InfoOutlined';

import { chatApi } from '../api/chatApi';
import { EntityName } from '../model/enums';
import type { TChatMessage } from '../model/types';

import {
  EditRibbon,
  ChatFormContainer,
  ChatTextbox,
  StyledFormControlLabel,
  StyledTooltip,
} from './styled';

type ChatFormProps = {
  activeChatMessage: TChatMessage | null;
  displayNotifyPMOnlyCheckbox: boolean;
  displayShowToContactPersonCheckbox: boolean;
  entityId: string;
  entityName: EntityName;
  onResetActiveChatMessage: () => void;
};

type ChatFormState = {
  message: string;
  notifyPMOnly?: boolean;
  showToContactPerson?: boolean;
};

const MAX_ALLOWED_CHARACTERS = 2000;

const ChatForm = ({
  activeChatMessage,
  displayNotifyPMOnlyCheckbox,
  displayShowToContactPersonCheckbox,
  entityId,
  entityName,
  onResetActiveChatMessage,
}: ChatFormProps) => {
  const { t } = useTranslation();

  const textboxRef = useRef<HTMLTextAreaElement | null>(null);

  const {
    control,
    handleSubmit,
    register,
    resetField,
    setValue,
    formState: { errors },
    watch,
  } = useForm<ChatFormState>({
    defaultValues: {
      message: '',
      notifyPMOnly: false,
      showToContactPerson: true,
    },
  });

  const [sendMessage, sendMessageRes] = chatApi.useSendMessageMutation();
  const [editMessage, editMessageRes] = chatApi.useEditMessageMutation();

  const disabled = sendMessageRes.isLoading || editMessageRes.isLoading;
  const isDisabledSubmitButton =
    !!errors.message || disabled || !watch('message');

  const _clearMessage = () => {
    resetField('message');
  };

  const submitForm = (formState: ChatFormState) => {
    if (activeChatMessage) {
      editMessage({
        chatMessageId: activeChatMessage.Id,
        message: formState.message,
      });
    } else {
      sendMessage({
        body: {
          Message: formState.message,
          ...(displayNotifyPMOnlyCheckbox && {
            IsNotifyPMOnly: formState.notifyPMOnly ?? false,
          }),
          ...(displayShowToContactPersonCheckbox && {
            IsShowToContactPerson: formState.showToContactPerson ?? false,
          }),
        },
        entityId,
        entityName,
      });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.ctrlKey && event.key === 'Enter') {
      handleSubmit(submitForm)(event);
    }
  };

  const handleDiscardChanges = () => {
    onResetActiveChatMessage();
    _clearMessage();
  };

  useEffect(() => {
    if (sendMessageRes.isSuccess || editMessageRes.isSuccess) {
      handleDiscardChanges();
    }
  }, [sendMessageRes.isSuccess, editMessageRes.isSuccess]);

  useEffect(() => {
    if (activeChatMessage) {
      setValue('message', activeChatMessage.Message);
      textboxRef.current?.focus();
    }
  }, [activeChatMessage]);

  const { ref: textareaRef, ...rest } = register('message', {
    maxLength: MAX_ALLOWED_CHARACTERS,
  });

  return (
    <ChatFormContainer component="form" onSubmit={handleSubmit(submitForm)}>
      <Stack spacing={1}>
        {activeChatMessage && (
          <EditRibbon>
            {t('MessageEdit')}
            <IconButton onClick={handleDiscardChanges}>
              <CloseIcon sx={{ fontSize: '0.875rem' }} />
            </IconButton>
          </EditRibbon>
        )}
        <ChatTextbox
          {...rest}
          disabled={disabled}
          placeholder={t('MessageWrite')}
          ref={(el: HTMLTextAreaElement | null) => {
            textareaRef(el);
            textboxRef.current = el;
          }}
          onKeyDown={handleKeyDown}
        />
      </Stack>

      {displayNotifyPMOnlyCheckbox && (
        <StyledFormControlLabel
          disabled={disabled}
          label={
            <>
              Älä lähetä hallitukselle viestiherätettä
              <StyledTooltip title="Kaikki Tehtävienhallinnan chat-viestit näkyvät aina koko hallitukselle portaalissa. Jos haluat herätteen uudesta viestistäsi lähtevän vain isännöintiin, valitse tämä.">
                <InfoOutlined
                  sx={{ marginLeft: '5px', fontSize: '1rem', color: '#646464' }}
                />
              </StyledTooltip>
            </>
          }
          control={
            <Controller
              control={control}
              name="notifyPMOnly"
              render={({ field: { onChange, value } }) => (
                <Checkbox checked={value} onChange={onChange} />
              )}
            />
          }
        />
      )}
      {displayShowToContactPersonCheckbox && (
        <StyledFormControlLabel
          disabled={disabled}
          label="Näytä ilmoittajalle"
          control={
            <Controller
              control={control}
              name="showToContactPerson"
              render={({ field: { onChange, value } }) => (
                <Checkbox checked={value} onChange={onChange} />
              )}
            />
          }
        />
      )}
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="flex-end"
        spacing={2}
      >
        <Button
          color="success"
          disabled={isDisabledSubmitButton}
          sx={{ alignSelf: 'flex-end' }}
          type="submit"
        >
          Lähetä
        </Button>
      </Stack>
    </ChatFormContainer>
  );
};

export default ChatForm;
